import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mic, RotateCcw, Send, Square } from "lucide-react";
import { api } from "../lib/api";

export default function Mote() {
    const { token } = useParams();
    const [state, setState] = useState({ status: "loading", data: null });
    const [phase, setPhase] = useState("idle");
    const [seconds, setSeconds] = useState(0);
    const [error, setError] = useState("");
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const blobRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        api.get(`/public/mote/${token}`)
            .then((r) => setState({ status: "ok", data: r.data }))
            .catch(() => setState({ status: "expired", data: null }));
        return () => clearInterval(timerRef.current);
    }, [token]);

    const start = async () => {
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
            rec.onstop = () => {
                blobRef.current = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                stream.getTracks().forEach((t) => t.stop());
                setPhase("recorded");
            };
            recorderRef.current = rec;
            rec.start();
            setSeconds(0);
            timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
            setPhase("recording");
        } catch {
            setError("Kunde inte komma åt mikrofonen – tillåt mikrofonåtkomst i webbläsaren och försök igen.");
        }
    };

    const stop = () => {
        clearInterval(timerRef.current);
        recorderRef.current?.stop();
    };

    const redo = () => {
        blobRef.current = null;
        setSeconds(0);
        setPhase("idle");
    };

    const send = async () => {
        if (!blobRef.current) return;
        setPhase("sending");
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", blobRef.current, "mote.webm");
            await api.post(`/public/mote/${token}/upload`, fd, { timeout: 180000 });
            setPhase("done");
        } catch {
            setError("Uppladdningen misslyckades – försök igen.");
            setPhase("recorded");
        }
    };

    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");

    return (
        <div className="min-h-screen canvas-bg flex items-center justify-center px-4 py-10" data-testid="mote-page">
            <div className="w-full max-w-md">
                <div className="glass-panel rounded-2xl p-8 shadow-2xl text-center">
                    <img src="/hamn-emblem.png" alt="HAMN" className="w-12 h-12 rounded-full border border-current/40 object-cover mx-auto mb-4" />
                    <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Mötesinspelning</p>
                    <h1 className="font-display font-extrabold text-xl tracking-tight text-slate-100 mb-4">
                        {state.data?.title || "Möte"}
                    </h1>

                    {state.status === "loading" && (
                        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Laddar …</div>
                    )}

                    {state.status === "expired" && (
                        <p data-testid="mote-expired" className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5 text-left">
                            Den här länken har gått ut eller stängts. Kontakta din konsult för en ny länk.
                        </p>
                    )}

                    {state.status === "ok" && phase !== "done" && (
                        <>
                            <p className="text-xs text-slate-500 leading-relaxed mb-6 text-left">
                                Spela in mötet direkt i webbläsaren. Ljudet transkriberas och av-identifieras automatiskt –
                                <span className="text-slate-300"> själva ljudfilen lagras aldrig</span>. Konsulten granskar
                                den av-identifierade texten innan den används. Genom att spela in godkänner du detta.
                            </p>

                            {phase === "idle" && (
                                <button data-testid="mote-record-button" onClick={start}
                                    className="w-24 h-24 rounded-full bg-red-500/15 border-2 border-red-500/60 text-red-400 flex items-center justify-center mx-auto hover:bg-red-500/25 hover:scale-105 transition-all">
                                    <Mic className="w-9 h-9" />
                                </button>
                            )}

                            {phase === "recording" && (
                                <div>
                                    <button data-testid="mote-stop-button" onClick={stop}
                                        className="w-24 h-24 rounded-full bg-red-500/25 border-2 border-red-500 text-red-400 flex items-center justify-center mx-auto animate-pulse hover:bg-red-500/35 transition-all">
                                        <Square className="w-8 h-8" />
                                    </button>
                                    <p data-testid="mote-timer" className="font-mono text-2xl text-red-400 mt-4">{mm}:{ss}</p>
                                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">Spelar in … klicka för att stoppa</p>
                                </div>
                            )}

                            {(phase === "recorded" || phase === "sending") && (
                                <div>
                                    <div className="rounded-xl border border-buoy/40 bg-buoy/10 px-4 py-3 mb-4">
                                        <p className="text-sm text-buoy font-semibold">Inspelning klar – {mm}:{ss}</p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">Skicka för transkribering, eller spela om.</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button data-testid="mote-send-button" onClick={send} disabled={phase === "sending"}
                                            className="flex-1 flex items-center justify-center gap-2 bg-current text-abyss font-semibold text-sm rounded-lg py-3 hover:bg-tide transition-colors disabled:opacity-50">
                                            {phase === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            {phase === "sending" ? "Transkriberar …" : "Skicka till transkribering"}
                                        </button>
                                        <button data-testid="mote-redo-button" onClick={redo} disabled={phase === "sending"}
                                            className="px-4 border border-border text-slate-400 rounded-lg hover:border-slate-500 transition-colors disabled:opacity-40">
                                            <RotateCcw className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {phase === "sending" && (
                                        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-3">
                                            Whisper transkriberar – kan ta upp till en minut
                                        </p>
                                    )}
                                </div>
                            )}

                            {error && <p data-testid="mote-error" className="text-sm text-red-400 mt-4">{error}</p>}
                        </>
                    )}

                    {phase === "done" && (
                        <div data-testid="mote-done" className="py-4">
                            <CheckCircle2 className="w-10 h-10 text-buoy mx-auto mb-3" />
                            <h2 className="font-display font-bold text-lg text-slate-100">Tack!</h2>
                            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
                                Inspelningen har transkriberats och av-identifierats. Ljudfilen är raderad.
                                Konsulten granskar texten innan den används i analysen.
                            </p>
                        </div>
                    )}
                </div>
                <p className="text-center font-mono text-[10px] uppercase tracking-widest text-slate-600 mt-6">
                    HAMN ATLAS · råinspelningar lagras aldrig
                </p>
            </div>
        </div>
    );
}
